import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, CreditCard, ExternalLink, Landmark, Pencil, RefreshCw, ShieldOff, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatUSD } from "@/lib/format";

const currentMonth = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
};

const chargeStatusPill = (s) => {
  const map = {
    pending_charge: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    processing: { label: "Processing", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    succeeded: { label: "Charged", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    failed: { label: "Failed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    refunded: { label: "Refunded", cls: "bg-slate-100 text-slate-600 border-slate-200" },
    manually_waived: { label: "Waived", cls: "bg-purple-50 text-purple-700 border-purple-200" },
    not_billable: { label: "Not billable", cls: "bg-slate-50 text-slate-500 border-slate-200" },
    // dealer-row aggregated statuses
    has_failures: { label: "Has failures", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    pending: { label: "Some pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    charged: { label: "All charged", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    no_activity: { label: "No activity", cls: "bg-slate-50 text-slate-500 border-slate-200" },
  };
  const m = map[s] || map.pending;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
};

const AdminBilling = () => {
  const [tab, setTab] = useState("preview");
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState([]);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownRows, setDrilldownRows] = useState([]);

  const [refundOpen, setRefundOpen] = useState(null); // row or null
  const [waiveOpen, setWaiveOpen] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Phase 11 — Payouts tab (BDC ACH + Dealer Stripe cards)
  const [payouts, setPayouts] = useState(null);

  const reloadPreview = async () => {
    setData(null); setDrilldown(null); setDrilldownRows([]);
    try {
      const { data } = await api.get(`/admin/billing/preview?month=${month}`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const reloadFailed = async () => {
    try {
      const { data } = await api.get("/admin/billing/failed-charges");
      setFailed(data.rows || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const reloadPayouts = async () => {
    setPayouts(null);
    try {
      const { data } = await api.get("/admin/payouts/overview");
      setPayouts(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { reloadPreview(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "failed") reloadFailed(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "payouts") reloadPayouts(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!drilldown) { setDrilldownRows([]); return; }
    api.get(`/admin/billing/appointments?month=${month}&dealer_id=${drilldown}`)
      .then((r) => setDrilldownRows(r.data.rows)).catch(() => setDrilldownRows([]));
  }, [drilldown, month]);

  const doRetry = async (row) => {
    if (!window.confirm(`Retry the $${(row.amount_cents || 30000) / 100} charge for ${row.customer_name}? Make sure the dealer has a valid payment method on file.`)) return;
    try {
      const { data } = await api.post(`/admin/billing/appointments/${row.appointment_id}/retry-charge`);
      toast.success(data.status === "succeeded" ? "Charge succeeded" : `Retry result: ${data.status}`);
      reloadFailed();
      reloadPreview();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitRefund = async () => {
    if (!refundOpen || !reason.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/billing/appointments/${refundOpen.appointment_id}/refund`, { reason });
      toast.success("Refund issued");
      setRefundOpen(null); setReason("");
      reloadPreview();
      if (tab === "failed") reloadFailed();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const submitWaive = async () => {
    if (!waiveOpen || !reason.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/billing/appointments/${waiveOpen.appointment_id}/waive`, { reason });
      toast.success("Charge waived");
      setWaiveOpen(null); setReason("");
      reloadPreview();
      if (tab === "failed") reloadFailed();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const submitAdjust = async () => {
    if (!adjustOpen || !reason.trim() || !adjustAmount) return;
    const cents = Math.round(parseFloat(adjustAmount) * 100);
    if (!Number.isFinite(cents) || cents < 0) {
      toast.error("Amount must be a non-negative number");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/billing/appointments/${adjustOpen.appointment_id}/adjust-amount`, {
        amount_cents: cents,
        reason,
      });
      toast.success("Amount adjusted");
      setAdjustOpen(null); setAdjustAmount(""); setReason("");
      reloadPreview();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const stripeLink = (piId) => piId ? `https://dashboard.stripe.com/test/payments/${piId}` : null;

  return (
    <AdminPage
      title="Billing"
      subtitle="Live Stripe test-mode charges. $300 per showed-up appointment. Retry, refund, or waive from here."
      testId="admin-billing-page"
      action={<Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="admin-billing-month" />}
    >
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 mb-5" data-testid="admin-billing-tabs">
        {[
          { id: "preview", label: "Preview" },
          { id: "failed", label: `Failed charges${failed.length ? ` (${failed.length})` : ""}` },
          { id: "payouts", label: "Payout accounts" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.id ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-900"}`}
            data-testid={`admin-billing-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "preview" && (
        <>
          {!data ? (
            <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-gold text-white rounded-2xl p-5" data-testid="admin-billing-grand-total">
                  <p className="text-[10px] uppercase tracking-widest">Gross — {data.month_label}</p>
                  <p className="mt-2 font-display text-3xl">{formatUSD(data.grand_total_gross, 0)}</p>
                  <p className="text-xs opacity-80 mt-1">${300} × {data.grand_total_showed_up} showed up</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-billing-refunded-total">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Refunds</p>
                  <p className="mt-2 font-display text-3xl text-rose-700">-{formatUSD(data.grand_total_refunded, 0)}</p>
                </div>
                <div className="bg-white border-2 border-emerald-500 rounded-2xl p-5" data-testid="admin-billing-net-total">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Net (after refunds)</p>
                  <p className="mt-2 font-display text-3xl text-emerald-700">{formatUSD(data.grand_total_net, 0)}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-5">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Dealers billable</p>
                  <p className="mt-2 font-display text-3xl">{data.rows.filter((r) => r.showed_up_count > 0).length} / {data.rows.length}</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-billing-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Dealership</th>
                      <th className="text-left px-4 py-3 font-semibold">City</th>
                      <th className="text-left px-4 py-3 font-semibold">Card</th>
                      <th className="text-right px-4 py-3 font-semibold">Events</th>
                      <th className="text-right px-4 py-3 font-semibold">Gross</th>
                      <th className="text-right px-4 py-3 font-semibold">Refunded</th>
                      <th className="text-right px-4 py-3 font-semibold">Net</th>
                      <th className="text-left px-4 py-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <React.Fragment key={r.dealer_id}>
                        <tr
                          className={`border-t border-slate-100 ${r.showed_up_count === 0 ? "opacity-60" : "hover:bg-slate-50 cursor-pointer"}`}
                          onClick={() => r.showed_up_count > 0 && setDrilldown(drilldown === r.dealer_id ? null : r.dealer_id)}
                          data-testid={`admin-billing-row-${r.dealer_id}`}
                        >
                          <td className="px-4 py-3 font-medium">{r.dealer_name}</td>
                          <td className="px-4 py-3 text-slate-600">{r.city || "—"}</td>
                          <td className="px-4 py-3 text-xs">
                            {r.payment_method_last4 ? `${(r.payment_method_last4 || "").toUpperCase()} •••• ${r.payment_method_last4}` : <span className="text-rose-600">No card</span>}
                          </td>
                          <td className="px-4 py-3 text-right">{r.showed_up_count}</td>
                          <td className="px-4 py-3 text-right text-slate-800 font-semibold">{formatUSD(r.gross_amount, 0)}</td>
                          <td className="px-4 py-3 text-right text-rose-700">{r.refunded_amount > 0 ? `-${formatUSD(r.refunded_amount, 0)}` : "—"}</td>
                          <td className="px-4 py-3 text-right text-emerald-700 font-bold">{formatUSD(r.net_amount, 0)}</td>
                          <td className="px-4 py-3">{chargeStatusPill(r.charge_status)}</td>
                        </tr>
                        {drilldown === r.dealer_id && (
                          <tr>
                            <td colSpan={8} className="bg-slate-50 px-6 py-3">
                              {drilldownRows.length === 0 ? <p className="text-xs text-slate-500">Loading…</p> : (
                                <table className="w-full text-xs">
                                  <thead className="text-slate-500">
                                    <tr>
                                      <th className="text-left py-1">Date</th>
                                      <th className="text-left">Customer</th>
                                      <th className="text-left">Vehicle</th>
                                      <th className="text-left">Status</th>
                                      <th className="text-left">Stripe</th>
                                      <th className="text-right">Amount</th>
                                      <th className="text-right">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {drilldownRows.map((li) => (
                                      <tr key={li.appointment_id} className="border-t border-slate-200" data-testid={`admin-billing-lineitem-${li.appointment_id}`}>
                                        <td className="py-1">{new Date(li.showed_up_at).toLocaleDateString()}</td>
                                        <td>{li.customer_name}</td>
                                        <td>{li.vehicle_label}</td>
                                        <td>{chargeStatusPill(li.charge_status || li.billing_status)}</td>
                                        <td>
                                          {li.stripe_payment_intent_id ? (
                                            <a href={stripeLink(li.stripe_payment_intent_id)} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline inline-flex items-center gap-1" data-testid={`admin-billing-stripe-link-${li.appointment_id}`}>
                                              {li.stripe_payment_intent_id.slice(0, 14)}… <ExternalLink size={10} />
                                            </a>
                                          ) : "—"}
                                        </td>
                                        <td className="text-right text-gold font-semibold">{formatUSD(li.amount, 0)}</td>
                                        <td className="text-right">
                                          <div className="inline-flex gap-1">
                                            {li.charge_status === "succeeded" && (
                                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); setRefundOpen(li); setReason(""); }} data-testid={`admin-billing-refund-btn-${li.appointment_id}`}>
                                                <Undo2 size={12} /> Refund
                                              </Button>
                                            )}
                                            {li.charge_status === "failed" && (
                                              <>
                                                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); doRetry(li); }} data-testid={`admin-billing-retry-btn-${li.appointment_id}`}>
                                                  <RefreshCw size={12} /> Retry
                                                </Button>
                                                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); setWaiveOpen(li); setReason(""); }} data-testid={`admin-billing-waive-btn-${li.appointment_id}`}>
                                                  <ShieldOff size={12} /> Waive
                                                </Button>
                                              </>
                                            )}
                                            {li.charge_status === "pending_charge" && (
                                              <>
                                                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); setAdjustOpen(li); setAdjustAmount(String(((li.amount_cents || 30000) / 100).toFixed(2))); setReason(""); }} data-testid={`admin-billing-adjust-btn-${li.appointment_id}`}>
                                                  <Pencil size={12} /> Adjust
                                                </Button>
                                                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); setWaiveOpen(li); setReason(""); }} data-testid={`admin-billing-waive-btn-${li.appointment_id}`}>
                                                  <ShieldOff size={12} /> Waive
                                                </Button>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    <tr className="bg-slate-900 text-white">
                      <td className="px-4 py-3 font-semibold" colSpan={3}>Total</td>
                      <td className="px-4 py-3 text-right font-semibold">{data.grand_total_showed_up}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatUSD(data.grand_total_gross, 0)}</td>
                      <td className="px-4 py-3 text-right text-rose-300">{data.grand_total_refunded > 0 ? `-${formatUSD(data.grand_total_refunded, 0)}` : "—"}</td>
                      <td className="px-4 py-3 text-right text-gold font-bold">{formatUSD(data.grand_total_net, 0)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {tab === "failed" && (
        <div className="bg-white border border-rose-200 rounded-2xl overflow-hidden" data-testid="admin-failed-charges-table">
          {failed.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No failed charges. All showed-up appointments are billing correctly.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-rose-50 text-rose-700 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Dealer</th>
                  <th className="text-left px-4 py-3 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold">Vehicle</th>
                  <th className="text-left px-4 py-3 font-semibold">Error</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-right px-4 py-3 font-semibold">Amount</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {failed.map((r) => (
                  <tr key={r.appointment_id} className="border-t border-slate-100" data-testid={`admin-failed-row-${r.appointment_id}`}>
                    <td className="px-4 py-3 font-medium">
                      {r.dealer_name}
                      {!r.has_payment_method && <div className="text-[10px] text-rose-600 flex items-center gap-1 mt-0.5"><AlertTriangle size={10} /> No card on file</div>}
                    </td>
                    <td className="px-4 py-3">{r.customer_name}</td>
                    <td className="px-4 py-3">{r.vehicle_summary}</td>
                    <td className="px-4 py-3 max-w-[240px]"><span className="text-rose-700 text-xs">{r.charge_error}</span></td>
                    <td className="px-4 py-3 text-slate-600">{new Date(r.showed_up_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatUSD((r.amount_cents || 30000) / 100, 0)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => doRetry(r)} data-testid={`admin-failed-retry-${r.appointment_id}`}>
                          <RefreshCw size={12} /> Retry
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setWaiveOpen(r); setReason(""); }} data-testid={`admin-failed-waive-${r.appointment_id}`}>
                          <ShieldOff size={12} /> Waive
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "payouts" && (
        <div className="space-y-6" data-testid="admin-payouts-panel">
          {!payouts ? (
            <div className="h-56 bg-slate-100 rounded-2xl animate-pulse" />
          ) : (
            <>
              {/* Roll-up counters */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-payouts-kpi-dealers-ready">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Dealers ready to charge</p>
                  <p className="mt-1 font-display text-2xl text-slate-900">{payouts.counts.dealers_ready} <span className="text-slate-400 text-sm">/ {payouts.counts.dealers_total}</span></p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-payouts-kpi-bdc-ready">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">BDC agents verified</p>
                  <p className="mt-1 font-display text-2xl text-emerald-700">{payouts.counts.bdc_ready} <span className="text-slate-400 text-sm">/ {payouts.counts.bdc_total}</span></p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-payouts-kpi-bdc-pending">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">BDC pending verify</p>
                  <p className="mt-1 font-display text-2xl text-amber-700">{payouts.counts.bdc_on_file_unverified}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Rate structure</p>
                  <p className="mt-1 text-xs text-slate-700 leading-snug">$300/showed-up billed to dealer · $50/showed-up paid to BDC</p>
                </div>
              </div>

              {/* Dealer Stripe cards */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-payouts-dealer-table">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <CreditCard size={16} className="text-brand" />
                  <h3 className="font-display text-base font-semibold text-slate-900">Dealer payout accounts</h3>
                  <span className="text-xs text-slate-500">(Stripe cards we charge)</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Dealership</th>
                      <th className="text-left px-4 py-2 font-semibold">City</th>
                      <th className="text-left px-4 py-2 font-semibold">Setup</th>
                      <th className="text-left px-4 py-2 font-semibold">Card on file</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.dealer_accounts.length === 0 ? (
                      <tr><td colSpan={4} className="p-6 text-center text-slate-500 text-sm">No dealers configured.</td></tr>
                    ) : payouts.dealer_accounts.map((d) => (
                      <tr key={d.dealer_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-payouts-dealer-${d.dealer_id}`}>
                        <td className="px-4 py-2 font-medium">{d.dealer_name}</td>
                        <td className="px-4 py-2 text-slate-600">{d.city || "—"}{d.state ? `, ${d.state}` : ""}</td>
                        <td className="px-4 py-2">
                          {d.payment_setup_status === "active" ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 size={12} /> Active</span>
                          ) : d.payment_setup_status === "pending" ? (
                            <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold"><AlertTriangle size={12} /> Pending</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-semibold"><AlertTriangle size={12} /> Not set up</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-slate-700">
                          {d.payment_method_last4 ? `${(d.payment_method_brand || "card").toUpperCase()} •••• ${d.payment_method_last4}` : <span className="text-rose-600">No card</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* BDC ACH accounts */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-payouts-bdc-table">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <Landmark size={16} className="text-brand" />
                  <h3 className="font-display text-base font-semibold text-slate-900">BDC payout accounts</h3>
                  <span className="text-xs text-slate-500">(ACH we send commissions to)</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Agent</th>
                      <th className="text-left px-4 py-2 font-semibold">Account holder</th>
                      <th className="text-left px-4 py-2 font-semibold">Bank</th>
                      <th className="text-left px-4 py-2 font-semibold">Routing</th>
                      <th className="text-left px-4 py-2 font-semibold">Account</th>
                      <th className="text-left px-4 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.bdc_accounts.length === 0 ? (
                      <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">No BDC agents on file.</td></tr>
                    ) : payouts.bdc_accounts.map((b) => (
                      <tr key={b.bdc_user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-payouts-bdc-${b.bdc_user_id}`}>
                        <td className="px-4 py-2">
                          <div className="font-medium text-slate-900">{b.full_name || "—"}</div>
                          <div className="text-xs text-slate-500">{b.email}</div>
                        </td>
                        <td className="px-4 py-2 text-slate-700">{b.account_holder_name || <span className="text-slate-400">—</span>}</td>
                        <td className="px-4 py-2 text-slate-700">{b.bank_name || <span className="text-slate-400">—</span>}</td>
                        <td className="px-4 py-2 text-xs font-mono">{b.routing_number || <span className="text-slate-400">—</span>}</td>
                        <td className="px-4 py-2 text-xs font-mono">{b.account_masked || <span className="text-slate-400">—</span>}</td>
                        <td className="px-4 py-2">
                          {b.verified ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold"><CheckCircle2 size={11} /> Verified</span>
                          ) : b.on_file ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-2 py-0.5 text-[11px] font-semibold"><AlertTriangle size={11} /> Awaiting verify</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 text-slate-500 px-2 py-0.5 text-[11px] font-semibold">Not on file</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Refund dialog */}
      <Dialog open={!!refundOpen} onOpenChange={(v) => { if (!v) setRefundOpen(null); }}>
        <DialogContent data-testid="admin-refund-dialog">
          <DialogHeader>
            <DialogTitle>Refund charge</DialogTitle>
            <DialogDescription>
              Refund the $300 charge for <strong>{refundOpen?.customer_name}</strong>. The appointment stays as &ldquo;showed up&rdquo; — only the payment reverses.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Reason</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer disputed — vehicle was not as described" data-testid="admin-refund-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={submitRefund} disabled={saving || !reason.trim()} data-testid="admin-refund-submit-btn">
              {saving ? "Refunding…" : "Confirm refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waive dialog */}
      <Dialog open={!!waiveOpen} onOpenChange={(v) => { if (!v) setWaiveOpen(null); }}>
        <DialogContent data-testid="admin-waive-dialog">
          <DialogHeader>
            <DialogTitle>Waive charge</DialogTitle>
            <DialogDescription>
              Mark this charge as waived. No Stripe call will be made. Use this for promotional credit or dispute resolution.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Reason</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Promotional credit approved by ops" data-testid="admin-waive-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaiveOpen(null)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover text-white" onClick={submitWaive} disabled={saving || !reason.trim()} data-testid="admin-waive-submit-btn">
              {saving ? "Waiving…" : "Confirm waive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 11 — adjust the default $300 charge amount before Stripe fires. */}
      <Dialog open={!!adjustOpen} onOpenChange={(open) => !open && setAdjustOpen(null)}>
        <DialogContent data-testid="admin-adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust charge amount</DialogTitle>
            <DialogDescription>
              Only allowed before the charge fires (status=pending or failed). Audited under
              <code className="ml-1 text-[11px]">admin.billing.amount_adjusted</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">New amount (USD)</Label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                className="mt-1 flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 font-mono"
                data-testid="admin-adjust-amount-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Reason (required)</Label>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Partial refund settlement pre-charge" data-testid="admin-adjust-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(null)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover text-white" onClick={submitAdjust} disabled={saving || !reason.trim() || !adjustAmount} data-testid="admin-adjust-submit-btn">
              {saving ? "Adjusting…" : "Save new amount"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default AdminBilling;
